import { parseBulletLines } from '@mss_creator/shared';
import { Tooltip } from '@pms-digital/design-system';
import type { ReactNode } from 'react';

export interface MatrixCellPreviewProps {
  /** Which cell this is — `<dimension> · Palier <n>`, or the levier name for an activation row. */
  header: string;
  /** The cell's raw content. An empty string renders the "Aucun contenu" state. */
  content: string;
  /** True while this cell's dimension-generation job is still running. */
  isPending?: boolean;
  /** Open discussions on the cell, when the viewer sees the markers (issue #19, planche 10); nothing for zero. */
  openNoteCount?: number;
  children: ReactNode;
  className?: string;
}

/**
 * The legible version of a matrix cell, shown on hover or focus in global view
 * (2026-08-25 design doc, P1-P6).
 *
 * Deliberately separate from `ui/tooltip.tsx`: that primitive is documented as
 * single-string content, two unrelated callers depend on that contract, and its
 * `max-w-xs text-caption` sizing is the opposite of what this needs — the entire
 * purpose here is legibility at a scale where the cell itself is not.
 *
 * The header line is not decoration. At the fit factors this mode produces
 * (~0.3 for a 30-dimension matrix) the row and column headers are illegible, so
 * without it the tooltip says what a cell contains but not which cell it is,
 * and the mode stops working as an orientation tool.
 *
 * `delayDuration={0}` because the requirement is that hovering shows the cell
 * immediately. Radix reuses one portal and swaps its content when the pointer
 * moves between triggers, so sweeping the grid re-renders content rather than
 * reopening a tooltip per cell.
 *
 * No `max-height`: Radix tooltip content is `pointer-events: none`, so it can
 * be neither scrolled nor hovered into — a height cap would silently clip the
 * longest cells with no way to reach the rest, which is worse than a tall
 * tooltip. Cell content is a few bullet lines in practice.
 */
export function MatrixCellPreview({
  header,
  content,
  isPending = false,
  openNoteCount = 0,
  children,
}: MatrixCellPreviewProps) {
  const lines = parseBulletLines(content);

  return (
    <Tooltip
      side="bottom"
      title={header}
      content={
        <div data-testid="matrix-cell-preview" className="w-72">
          {lines.length > 0 ? (
            <div className="space-y-1 leading-relaxed">
              {lines.map((line, index) =>
                line.hasBullet ? (
                  // biome-ignore lint/suspicious/noArrayIndexKey: bullet lines have no stable identity
                  <ul key={index} className="list-inside list-disc">
                    <li>{line.text}</li>
                  </ul>
                ) : (
                  // biome-ignore lint/suspicious/noArrayIndexKey: bullet lines have no stable identity
                  <p key={index}>{line.text}</p>
                ),
              )}
            </div>
          ) : (
            <p className="text-gray-700 italic">
              {isPending ? 'Génération en cours…' : 'Aucun contenu'}
            </p>
          )}
          {openNoteCount > 0 && (
            <p className="mt-1 border-gray-100 border-t pt-1 font-semibold text-bleu-nuit text-caption">
              {openNoteCount === 1 ? '1 note ouverte' : `${openNoteCount} notes ouvertes`}
            </p>
          )}
        </div>
      }
    >
      {children}
    </Tooltip>
  );
}
